import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Divider,
  Box,
  useTheme,
  Slide,
  Tooltip,
  IconButton,
  MenuItem,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { CATEGORY_OPTIONS } from "../constants/expenseCategories";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function ReviewBeforeSaveModal({
  open,
  data,
  onClose,
  onSave,
  setData,
}) {
  const theme = useTheme();

  useEffect(() => {
    if (data?.category) {
      setError(""); // Clear any lingering category error if pre-filled
    }
  }, [data?.category]);

  const [error, setError] = useState("");

  if (!data) return null;

  const handleChange = (field) => (e) => {
    setData((prev) => ({
      ...prev,
      [field]:
        field === "notes" ? e.target.value : parseFloat(e.target.value) || null,
    }));
  };

  const handleSave = () => {
    if (!data.category) {
      setError("Please select a category before saving.");
      return;
    }
    setError("");
    onSave(data);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Transition}
      keepMounted
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <ReceiptLongIcon color="primary" />
        Review Before Saving
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2, pb: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            mb: 2,
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="body2" sx={{ flex: 1 }}>
            We extracted totals from your receipt. This is just a quick check to
            ensure everything is accurate before we save it to your records.
          </Typography>
          <Tooltip
            title="We show this review step to avoid saving incorrect values from OCR."
            arrow
          >
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Total (incl. VAT)"
            type="number"
            fullWidth
            required
            value={data.invoiceTotal ?? ""}
            onChange={handleChange("invoiceTotal")}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="VAT (optional)"
            type="number"
            fullWidth
            value={data.vatAmount ?? ""}
            onChange={handleChange("vatAmount")}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Subtotal (optional)"
            type="number"
            fullWidth
            value={data.subtotal ?? ""}
            onChange={handleChange("subtotal")}
            inputProps={{ min: 0 }}
          />
          
          {/* Show Net Cost Calculation for User Assurance */}
          <Box sx={{ p: 2, bgcolor: "background.default", borderRadius: 2, border: "1px dashed", borderColor: "divider" }}>
             <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
               NET COST (Total - VAT)
             </Typography>
             <Typography variant="h6" color="primary.main" fontWeight={700}>
               €{((parseFloat(data.invoiceTotal || 0) - parseFloat(data.vatAmount || 0))).toFixed(2)}
             </Typography>
             <Typography variant="caption" color="text.secondary">
               This is the amount that will be written off as an expense.
             </Typography>
          </Box>
          <TextField
            select
            label="Category"
            fullWidth
            required
            value={data.category || ""}
            onChange={(e) =>
              setData((prev) => ({ ...prev, category: e.target.value }))
            }
            error={Boolean(error)}
            helperText={error}
          >
            {CATEGORY_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Notes (optional)"
            fullWidth
            multiline
            minRows={2}
            value={data.notes || ""}
            onChange={handleChange("notes")}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} sx={{ minWidth: 140 }}>
          Save Receipt
        </Button>
      </DialogActions>
    </Dialog>
  );
}
